import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addendumMacrosService,
  type AddendumMacroInput,
  type AddendumMacroType,
} from "../../services/admin/addendumMacros.service";

const QUERY_KEY = ["admin", "addendum-macros"];

function showMacroError() {
  notifications.show({
    title: "Report macro was not changed",
    message: "Try again or refresh the page.",
    color: "red",
  });
}

export function useAddendumMacrosAdmin(macroType?: AddendumMacroType) {
  return useQuery({
    queryKey: [...QUERY_KEY, macroType || "all"],
    queryFn: () => addendumMacrosService.getAll(macroType),
  });
}

function useInvalidateAddendumMacros() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ["case"] });
  };
}

export function useCreateAddendumMacro() {
  const invalidate = useInvalidateAddendumMacros();
  return useMutation({
    mutationFn: (input: AddendumMacroInput) =>
      addendumMacrosService.create(input),
    onSuccess: () => {
      invalidate();
      notifications.show({
        title: "Report macro saved",
        message: "The macro is now available to pathologists.",
        color: "green",
      });
    },
    onError: showMacroError,
  });
}

export function useUpdateAddendumMacro() {
  const invalidate = useInvalidateAddendumMacros();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: Partial<AddendumMacroInput>;
    }) => addendumMacrosService.update(id, input),
    onSuccess: () => {
      invalidate();
      notifications.show({
        title: "Report macro updated",
        message: "The saved changes are now available.",
        color: "green",
      });
    },
    onError: showMacroError,
  });
}

export function useDeleteAddendumMacro() {
  const invalidate = useInvalidateAddendumMacros();
  return useMutation({
    mutationFn: addendumMacrosService.delete,
    onSuccess: () => {
      invalidate();
      notifications.show({
        title: "Report macro deleted",
        message: "The macro is no longer available.",
        color: "green",
      });
    },
    onError: showMacroError,
  });
}
